/**
 * PDFs for the four quality-area forms — Job Hazard Analysis, Concrete Field
 * Testing, Equipment Safety Inspection & Repair Report, Daily Report.
 *
 * Same approach as ./mix-design-pdf.ts, and for the same reasons: each is a
 * self-contained HTML document with an embedded stylesheet, handed to the
 * browser's own print dialog (Save as PDF). No PDF library is bundled, and
 * the text stays real text — selectable, searchable, and properly laid out.
 *
 * Everything except the four `print…` entry points is PURE, which is what
 * makes the layout unit-testable without a DOM.
 *
 * The letterhead is SCI's own, transcribed from the printed sheet — the same
 * block ./mix-design-pdf.ts carries. The four builders below share it, plus a
 * field-table helper and a log-table helper, plus one stylesheet, so the four
 * documents cannot drift apart from each other in look or in escaping
 * discipline.
 *
 * No acceptance verdicts: these forms record what was observed, not a
 * judgement, and neither does this module. The one number it does print that
 * looks like a verdict — the equipment inspection's Requires-Repair count —
 * is deliberately plain text, never styled or worded as a pass or fail.
 */

import { EQUIPMENT_CHECKLIST_GROUP_LABELS, EQUIPMENT_CHECKLIST_LEGEND } from '../seed-data';
import { resolveRowRange } from '../calc/contract';
import { totalCubes } from '../calc/pnl';
import { cubes } from '../money';
import {
  INSPECTION_STATUS_LABELS,
  type ContractReport,
  type ContractReportActivity,
  type ContractReportEquipment,
  type ContractReportOperator,
  type DailyReport,
  type DailyReportActivity,
  type DailyReportDelivery,
  type DailyReportPerson,
  type EquipmentChecklistGroup,
  type EquipmentInspection,
  type EquipmentInspectionItem,
  type FieldTestLog,
  type FieldTestRow,
  type Jha,
  type JhaStep,
  type PublicContractReportHire
} from '../types';
import { escapeHtml, printHtmlDocument, sciLogoHtml, type PrintOptions } from './pdf';

/* ==========================================================================
 * Shared: escaping, letterhead, stylesheet, table helpers
 * ======================================================================== */

/** Empty values print as an em dash so a blank line is visibly blank, not lost. */
const EMPTY = '&mdash;';

function text(value: unknown): string {
  if (value === null || value === undefined || value === '') return EMPTY;
  return escapeHtml(value);
}

/**
 * The letterhead reproduced on every printed sheet in this feature: the mark,
 * centred, and nothing else.
 *
 * The postal address and phone number that used to sit here were removed
 * deliberately — these are field records a client reads, not correspondence,
 * and SCI's contact details are not what the reader is looking for on them.
 *
 * The mark IS the wordmark, so no company name is typeset beside it. The name
 * is not lost: it is the image's alt text, which keeps this `h1` reading
 * "Sustainable Concrete Inc." to a screen reader and to anything that extracts
 * text from the PDF.
 */
const LETTERHEAD = [
  '<div class="letterhead">',
  `<h1>${sciLogoHtml(58)}</h1>`,
  '</div>'
].join('\n');

export const QUALITY_PRINT_STYLESHEET = `
  @page { size: letter portrait; margin: 16mm 14mm; }
  * { box-sizing: border-box; }
  body {
    margin: 0;
    color: #10161d;
    background: #fff;
    font: 11pt/1.4 -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif;
  }
  .doc { max-width: 190mm; margin: 0 auto; }
  /*
    The mark alone, centred. The padding above it is deliberate: @page already
    reserves a margin, but the mark reads as clipped when it starts hard against
    the top of the text block, so it is dropped a little further down the page.
  */
  .letterhead { padding-top: 4mm; margin-bottom: 8mm; text-align: center; }
  .letterhead h1 { margin: 0; font-size: 20pt; letter-spacing: -0.01em; }
  .letterhead h1 img { margin-inline: auto; }
  h2 { margin: 6mm 0 2mm; font-size: 12pt; border-bottom: 1.5pt solid #10161d; padding-bottom: 1mm; }
  table { border-collapse: collapse; width: 100%; font-size: 10pt; }
  th, td { border: 0.75pt solid #6b7784; padding: 1.6mm 2mm; text-align: left; vertical-align: top; }
  th { background: #eceff2; font-weight: 700; }
  .fields td.k { width: 32%; font-weight: 600; background: #f5f7f9; }
  .fields td { white-space: pre-wrap; }
  .notes { white-space: pre-wrap; }
  .meta { font-size: 9.5pt; color: #46525f; margin: 1mm 0; }
  .foot { margin-top: 8mm; font-size: 8.5pt; color: #46525f; }
  /* Repeat the table header on every page of a long sheet. */
  thead { display: table-header-group; }
  tr { break-inside: avoid; }
`;

type Field = readonly [label: string, value: unknown];

function fieldTable(fields: readonly Field[]): string {
  const rows = fields
    .map(([label, value]) => `<tr><td class="k">${escapeHtml(label)}</td><td>${text(value)}</td></tr>`)
    .join('');
  return `<table class="fields"><tbody>${rows}</tbody></table>`;
}

/**
 * One summary row under a table: a label spanning the columns before
 * `column`, the value under that column, and blank cells after it — so the
 * total sits directly beneath the numbers it adds up.
 */
interface TableFoot {
  label: string;
  /** Zero-based index of the column the value sits under. */
  column: number;
  value: unknown;
}

function logTable(columns: readonly string[], rows: readonly (readonly unknown[])[], foot?: TableFoot): string {
  const head = columns.map((label) => `<th>${escapeHtml(label)}</th>`).join('');
  const body = rows
    .map((cells) => `<tr>${cells.map((value) => `<td>${text(value)}</td>`).join('')}</tr>`)
    .join('');
  let tfoot = '';
  if (foot) {
    const after = columns.length - foot.column - 1;
    tfoot =
      `<tfoot><tr><th colspan="${foot.column}">${escapeHtml(foot.label)}</th><th>${text(foot.value)}</th>` +
      (after > 0 ? `<th colspan="${after}"></th>` : '') +
      '</tr></tfoot>';
  }
  return `<table class="log"><thead><tr>${head}</tr></thead><tbody>${body}</tbody>${tfoot}</table>`;
}

/**
 * A heading plus a table of rows — or nothing at all when there are no rows.
 * A record with no rows in a section omits that section's heading rather
 * than printing an empty table with a lonely header row.
 */
function section(
  heading: string,
  columns: readonly string[],
  rows: readonly (readonly unknown[])[],
  foot?: TableFoot
): string {
  if (rows.length === 0) return '';
  return `<h2>${escapeHtml(heading)}</h2>${logTable(columns, rows, foot)}`;
}

/** The total row under an Activities table: the day's concrete, in m³. */
function cubesFoot(activities: readonly { amount_supplied: string }[]): TableFoot {
  return { label: 'Total', column: 4, value: cubes(totalCubes(activities.map((a) => a.amount_supplied))) };
}

function footer(exportedAt: string): string {
  return `<p class="foot">Printed from SCI Quality on ${escapeHtml(exportedAt)}.</p>`;
}

function wrapHtml(title: string, body: string): string {
  return [
    '<!doctype html>',
    '<html lang="en">',
    '<head>',
    '<meta charset="utf-8">',
    '<meta name="viewport" content="width=device-width, initial-scale=1">',
    `<title>${escapeHtml(title)}</title>`,
    `<style>${QUALITY_PRINT_STYLESHEET}</style>`,
    '</head>',
    '<body>',
    body,
    '</body>',
    '</html>',
    ''
  ].join('\n');
}

/* ==========================================================================
 * Job Hazard Analysis
 * ======================================================================== */

export interface JhaPrintInput {
  jha: Jha;
  steps: readonly JhaStep[];
  /** Stamped in the document footer. Defaults to now. */
  exported_at?: string;
}

export function jhaPrintTitle(input: JhaPrintInput): string {
  const desc = input.jha.work_description.trim();
  return desc === '' ? 'Job Hazard Analysis' : desc;
}

/** Just the `<body>` inner HTML — usable for an in-page preview. */
export function buildJhaPrintBody(input: JhaPrintInput): string {
  const { jha } = input;
  const exportedAt = input.exported_at ?? new Date().toISOString();

  const parts: string[] = [
    '<div class="doc">',
    LETTERHEAD,
    '<h2>Job Hazard Analysis</h2>',
    fieldTable([
      ['Jobsite', jha.jobsite],
      ['Work description', jha.work_description],
      ['Location', jha.location],
      ['Scheduled start date', jha.scheduled_start_date],
      ['Attendees', jha.attendees]
    ]),
    section(
      'Steps',
      ['Sequence of Work', 'Potential Hazards', 'Method for Eliminating or Controlling and Person Responsible'],
      input.steps.map((step) => [step.sequence_of_work, step.potential_hazards, step.control_method])
    ),
    fieldTable([
      ['Prepared by', jha.prepared_by],
      ['Prepared date', jha.prepared_date],
      ['Reviewed by', jha.reviewed_by],
      ['Reviewed date', jha.reviewed_date]
    ]),
    footer(exportedAt),
    '</div>'
  ];

  return parts.filter((part) => part !== '').join('\n');
}

export function buildJhaPrintHtml(input: JhaPrintInput): string {
  return wrapHtml(jhaPrintTitle(input), buildJhaPrintBody(input));
}

/** Build the JHA and open the print / Save-as-PDF dialog. */
export function printJha(input: JhaPrintInput, options: PrintOptions = {}): boolean {
  return printHtmlDocument(buildJhaPrintHtml(input), options);
}

/* ==========================================================================
 * Concrete Field Testing
 * ======================================================================== */

export interface FieldTestPrintInput {
  field_test_log: FieldTestLog;
  rows: readonly FieldTestRow[];
  /** Stamped in the document footer. Defaults to now. */
  exported_at?: string;
}

export function fieldTestPrintTitle(input: FieldTestPrintInput): string {
  const title = input.field_test_log.title.trim();
  return title === '' ? 'Concrete Field Testing' : title;
}

/** Just the `<body>` inner HTML — usable for an in-page preview. */
export function buildFieldTestPrintBody(input: FieldTestPrintInput): string {
  const log = input.field_test_log;
  const exportedAt = input.exported_at ?? new Date().toISOString();
  const title = log.title.trim() === '' ? 'Concrete Field Testing' : log.title;

  const parts: string[] = [
    '<div class="doc">',
    LETTERHEAD,
    `<h2>${escapeHtml(title)}</h2>`,
    section(
      'Cylinder sets',
      [
        'Date Cast',
        'Truck No.',
        'No. Cast',
        'Pile Number',
        'Location',
        'Mix',
        'Slump',
        'Temperature',
        'De Moulded',
        'Specimen',
        '7 Day',
        '14 Day',
        '28 Day'
      ],
      input.rows.map((row) => [
        row.date_cast,
        row.truck_no,
        row.no_cast,
        row.pile_number,
        row.location,
        row.mix,
        row.slump_mm,
        row.temperature_c,
        row.de_moulded,
        row.specimen,
        row.break_7_day,
        row.break_14_day,
        row.break_28_day
      ])
    )
  ];

  if (log.notes.trim() !== '') {
    parts.push('<h2>Notes</h2>', `<p class="notes">${escapeHtml(log.notes)}</p>`);
  }

  parts.push(footer(exportedAt), '</div>');

  return parts.filter((part) => part !== '').join('\n');
}

export function buildFieldTestPrintHtml(input: FieldTestPrintInput): string {
  return wrapHtml(fieldTestPrintTitle(input), buildFieldTestPrintBody(input));
}

/** Build the field test log and open the print / Save-as-PDF dialog. */
export function printFieldTest(input: FieldTestPrintInput, options: PrintOptions = {}): boolean {
  return printHtmlDocument(buildFieldTestPrintHtml(input), options);
}

/* ==========================================================================
 * Equipment Safety Inspection & Repair Report
 * ======================================================================== */

export interface EquipmentPrintInput {
  equipment_inspection: EquipmentInspection;
  items: readonly EquipmentInspectionItem[];
  /** Stamped in the document footer. Defaults to now. */
  exported_at?: string;
}

/** The five printed groups, in printed order. */
const EQUIPMENT_GROUP_ORDER: readonly EquipmentChecklistGroup[] = [
  'outside',
  'engine',
  'cab',
  'cab_continued',
  'fluids'
];

export function equipmentPrintTitle(input: EquipmentPrintInput): string {
  const insp = input.equipment_inspection;
  const parts = [insp.equipment_no.trim(), insp.inspection_date.trim()].filter((part) => part !== '');
  return parts.length === 0 ? 'Equipment Inspection' : parts.join(' ');
}

function equipmentGroupSection(
  group: EquipmentChecklistGroup,
  items: readonly EquipmentInspectionItem[]
): string {
  const rows = items.filter((item) => item.group_name === group);
  return section(
    EQUIPMENT_CHECKLIST_GROUP_LABELS[group],
    ['Item', 'Status'],
    rows.map((item) => [item.label, INSPECTION_STATUS_LABELS[item.status]])
  );
}

/** Just the `<body>` inner HTML — usable for an in-page preview. */
export function buildEquipmentPrintBody(input: EquipmentPrintInput): string {
  const insp = input.equipment_inspection;
  const exportedAt = input.exported_at ?? new Date().toISOString();
  // Items whose status is 'rr', across every group. Printed once, as a plain
  // count — never coloured or worded as a pass or fail. See the file header.
  const rrCount = input.items.filter((item) => item.status === 'rr').length;

  const parts: string[] = [
    '<div class="doc">',
    LETTERHEAD,
    '<h2>Equipment Safety Inspection &amp; Repair Report</h2>',
    fieldTable([
      ['Company', insp.company],
      ['Location', insp.location],
      ['Job #', insp.job_no],
      ['Equipment #', insp.equipment_no],
      ['Hour meter', insp.hour_meter],
      ['Date', insp.inspection_date],
      ['Shift', insp.shift],
      ['Job name', insp.job_name],
      ['Type', insp.equipment_type],
      ['Mileage', insp.mileage]
    ]),
    // Printed before the groups, not after: a reader who hits five tables of
    // bare N/A / OK / RR codes before anything defines them has to read the
    // whole checklist twice. The live form puts the legend first for the
    // same reason.
    `<p class="meta">${escapeHtml(EQUIPMENT_CHECKLIST_LEGEND)}</p>`,
    `<p class="meta"><strong>${rrCount}</strong> item${rrCount === 1 ? '' : 's'} marked Requires Repair.</p>`,
    ...EQUIPMENT_GROUP_ORDER.map((group) => equipmentGroupSection(group, input.items))
  ];

  if (insp.defects_explanation.trim() !== '') {
    parts.push('<h2>Explanation of defects</h2>', `<p class="notes">${escapeHtml(insp.defects_explanation)}</p>`);
  }

  parts.push(
    '<h2>Repairs</h2>',
    fieldTable([
      ['Repairs needed', insp.repairs_needed],
      ['Repairs not needed', insp.repairs_not_needed]
    ]),
    '<h2>Signatures</h2>',
    fieldTable([
      ['Operator signature', insp.operator_signature],
      ['Operator date', insp.operator_date],
      ['Repairs completed by', insp.repairs_completed_by],
      ['Repairs completed date', insp.repairs_completed_date],
      ['Mechanic signature', insp.mechanic_signature],
      ['Mechanic date', insp.mechanic_date]
    ]),
    footer(exportedAt),
    '</div>'
  );

  return parts.filter((part) => part !== '').join('\n');
}

export function buildEquipmentPrintHtml(input: EquipmentPrintInput): string {
  return wrapHtml(equipmentPrintTitle(input), buildEquipmentPrintBody(input));
}

/** Build the equipment inspection and open the print / Save-as-PDF dialog. */
export function printEquipment(input: EquipmentPrintInput, options: PrintOptions = {}): boolean {
  return printHtmlDocument(buildEquipmentPrintHtml(input), options);
}

/* ==========================================================================
 * Daily Report
 * ======================================================================== */

export interface DailyReportPrintInput {
  daily_report: DailyReport;
  personnel: readonly DailyReportPerson[];
  deliveries: readonly DailyReportDelivery[];
  activities: readonly DailyReportActivity[];
  /** Standby, Delivery, Small load fee, ... — which and how many. No money on the sheet. */
  other_activities?: readonly { activity: string; quantity: string }[];
  /** Stamped in the document footer. Defaults to now. */
  exported_at?: string;
}

export function dailyReportPrintTitle(input: DailyReportPrintInput): string {
  const report = input.daily_report;
  const parts = [report.project.trim(), report.report_date.trim()].filter((part) => part !== '');
  return parts.length === 0 ? 'Daily Report' : parts.join(' ');
}

/** Just the `<body>` inner HTML — usable for an in-page preview. */
export function buildDailyReportPrintBody(input: DailyReportPrintInput): string {
  const report = input.daily_report;
  const exportedAt = input.exported_at ?? new Date().toISOString();

  const parts: string[] = [
    '<div class="doc">',
    LETTERHEAD,
    '<h2>Daily Report</h2>',
    fieldTable([
      ['Project', report.project],
      ['Weather', report.weather],
      ['Date', report.report_date],
      ['Ticket #', report.ticket_no]
    ]),
    section(
      'Personnel',
      ['Name', 'Time in', 'Time out'],
      input.personnel.map((person) => [person.name, person.time_in, person.time_out])
    ),
    section(
      'Deliveries',
      ['Material', 'Amount'],
      input.deliveries.map((delivery) => [delivery.material, delivery.amount])
    ),
    section(
      'Activities',
      ['Piling #', 'Unit #', 'Start', 'Stop', 'Amount supplied', 'Admixtures used', 'Mix design'],
      input.activities.map((activity) => [
        activity.piling_number,
        activity.unit_number,
        activity.start_time,
        activity.stop_time,
        activity.amount_supplied,
        activity.admixtures_used,
        activity.water_used
      ]),
      cubesFoot(input.activities)
    ),
    section(
      'Other activities',
      ['Activity', 'How many'],
      (input.other_activities ?? [])
        .filter((row) => row.activity.trim() !== '' || row.quantity.trim() !== '')
        .map((row) => [row.activity, row.quantity])
    )
  ];

  if (report.notes.trim() !== '') {
    parts.push('<h2>Notes</h2>', `<p class="notes">${escapeHtml(report.notes)}</p>`);
  }

  parts.push(
    fieldTable([
      ['Prepared by', report.prepared_by],
      ['Reviewed by', report.reviewed_by]
    ]),
    footer(exportedAt),
    '</div>'
  );

  return parts.filter((part) => part !== '').join('\n');
}

export function buildDailyReportPrintHtml(input: DailyReportPrintInput): string {
  return wrapHtml(dailyReportPrintTitle(input), buildDailyReportPrintBody(input));
}

/** Build the daily report and open the print / Save-as-PDF dialog. */
export function printDailyReport(input: DailyReportPrintInput, options: PrintOptions = {}): boolean {
  return printHtmlDocument(buildDailyReportPrintHtml(input), options);
}

/* ==========================================================================
 * Contract Report
 * ======================================================================== */

export interface ContractReportPrintInput {
  contract_report: ContractReport;
  equipment: readonly ContractReportEquipment[];
  operators: readonly ContractReportOperator[];
  activities: readonly ContractReportActivity[];
  /**
   * What was hired in. Typed as `PublicContractReportHire`, NEVER
   * `ContractReportHire` — the admin-only `cost_per_day` field does not
   * exist on this type, so it cannot reach this module even by a caller's
   * mistake. Optional so a caller with nothing to say about hires still
   * compiles: an absent list prints no Hired in section, same as an empty
   * one (`ContractReportInput.hires` in calc/contract.ts follows the same
   * rule, for the same reason).
   */
  hires?: readonly PublicContractReportHire[];
  /** Stamped in the document footer. Defaults to now. */
  exported_at?: string;
}

/** The printed word for a hire's `kind` — the same two options the form's own `<select>` offers. */
const HIRE_KIND_LABELS: Record<PublicContractReportHire['kind'], string> = {
  equipment: 'Equipment',
  subcontractor: 'Subcontractor'
};

/** The printed word for an equipment row's billing unit; an absent value reads as Day. */
const RATE_UNIT_LABELS: Record<'day' | 'week' | 'month', string> = {
  day: 'Day',
  week: 'Week',
  month: 'Month'
};

export function contractReportPrintTitle(input: ContractReportPrintInput): string {
  const report = input.contract_report;
  const parts = [report.ticket_no.trim(), report.report_date.trim()].filter((part) => part !== '');
  return parts.length === 0 ? 'Contract Report' : parts.join(' ');
}

/**
 * Just the `<body>` inner HTML — usable for an in-page preview.
 *
 * Four independent child tables in printed order — equipment, hired in,
 * operators, activities — the same order the live form uses. The activities
 * table shares the Daily Report's own seven columns exactly, so a pour reads
 * identically wherever it happened. Carries NO money — no rate, no cost, no
 * total, no amount — the same invariant the live form holds: the crew
 * records facts, admin prices them elsewhere. `input.hires`'s own type
 * (`PublicContractReportHire`) is what actually enforces this for hires: a
 * hire's `cost_per_day` cannot be read here because it does not exist on the
 * type this function accepts.
 *
 * Equipment and hire rows bill on their own dates, falling back to the
 * card's own range when blank — `resolveRowRange` (calc/contract.ts) is the
 * ONE place that fallback rule lives, so the day count shown here can never
 * disagree with the day count that bills.
 */
export function buildContractReportPrintBody(input: ContractReportPrintInput): string {
  const report = input.contract_report;
  const exportedAt = input.exported_at ?? new Date().toISOString();

  // The card's own range — the same fallback `priceContractReport`
  // (calc/contract.ts) computes: a blank end_date means a one-day card on
  // report_date alone.
  const cardStart = report.report_date;
  const cardEnd = report.end_date || report.report_date;

  const fields: Field[] = [
    ['Date', report.report_date],
    ['End date', report.end_date],
    ['Ticket #', report.ticket_no]
  ];
  if (report.cancelled === 1) {
    fields.push(['Cancelled', 'Yes']);
    if (report.cancellation_note.trim() !== '') {
      fields.push(['Cancellation note', report.cancellation_note]);
    }
  }

  const parts: string[] = [
    '<div class="doc">',
    LETTERHEAD,
    '<h2>Contract Report</h2>',
    fieldTable(fields),
    section(
      'Equipment',
      ['Equipment', 'Start', 'End', 'Bill by', 'Days'],
      input.equipment.map((row) => {
        const range = resolveRowRange(row.start_date ?? '', row.end_date ?? '', cardStart, cardEnd);
        return [row.equipment, row.start_date, row.end_date, RATE_UNIT_LABELS[row.rate_unit ?? 'day'], range.days];
      })
    ),
    section(
      'Hired in',
      ['Name', 'Kind', 'Start', 'End', 'Days'],
      (input.hires ?? []).map((row) => {
        const range = resolveRowRange(row.start_date, row.end_date, cardStart, cardEnd);
        return [row.name, HIRE_KIND_LABELS[row.kind], row.start_date, row.end_date, range.days];
      })
    ),
    section(
      'Operators',
      ['Name', 'Days', 'Time in', 'Time out'],
      input.operators.map((row) => [row.name, row.days, row.time_in, row.time_out])
    ),
    section(
      'Activities',
      ['Piling #', 'Unit #', 'Start', 'Stop', 'Amount supplied', 'Admixtures used', 'Mix design'],
      input.activities.map((activity) => [
        activity.piling_number,
        activity.unit_number,
        activity.start_time,
        activity.stop_time,
        activity.amount_supplied,
        activity.admixtures_used,
        activity.water_used
      ]),
      cubesFoot(input.activities)
    )
  ];

  if (report.notes.trim() !== '') {
    parts.push('<h2>Notes</h2>', `<p class="notes">${escapeHtml(report.notes)}</p>`);
  }

  parts.push(
    fieldTable([
      ['Prepared by', report.prepared_by],
      ['Reviewed by', report.reviewed_by]
    ]),
    footer(exportedAt),
    '</div>'
  );

  return parts.filter((part) => part !== '').join('\n');
}

export function buildContractReportPrintHtml(input: ContractReportPrintInput): string {
  return wrapHtml(contractReportPrintTitle(input), buildContractReportPrintBody(input));
}

/** Build the contract report and open the print / Save-as-PDF dialog. */
export function printContractReport(input: ContractReportPrintInput, options: PrintOptions = {}): boolean {
  return printHtmlDocument(buildContractReportPrintHtml(input), options);
}
